import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Megaphone, HandCoins, 
  Presentation, FileText, Users, Settings, X,
  ChevronDown, ChevronRight
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const Sidebar = ({ isOpen, setIsOpen }: SidebarProps) => {
  const location = useLocation();
  // Kaunsa submenu khula hai, usko track karne ke liye
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  // Naya Data Structure jisme subItems add kiye hain
  const menuItems = [
    { name: 'Dashboard', path: '/n-jio-sevaa/dashboard', icon: LayoutDashboard },
    { 
      name: 'Campaigns', 
      icon: Megaphone,
      subItems: [
        { name: 'Create', path: '/n-jio-sevaa/campaigns/create' },
        { name: 'Live', path: '/n-jio-sevaa/campaigns/live' }
      ]
    },
    { 
      name: 'Donations', 
      icon: HandCoins,
      subItems: [
        { name: 'Overview', path: '/n-jio-sevaa/donations/overview' },
        { name: 'Donors', path: '/n-jio-sevaa/donations/donors' }
      ]
    },
  
    { 
      name: 'Blogs', 
      icon: FileText,
      subItems: [
        { name: 'Create', path: '/n-jio-sevaa/blogs/create' },
        { name: 'All Blogs', path: '/n-jio-sevaa/blogs/all' }
      ]
    },
    { 
      name: 'Galleries', 
      icon: FileText,
      subItems: [
        { name: 'Add Video', path: '/n-jio-sevaa/galleries/video' },
        { name: 'Add Images', path: '/n-jio-sevaa/galleries/images' },
        { name: 'Library', path: '/n-jio-sevaa/galleries/library' }  
      ]
    },
    { 
      name: 'Users', 
      icon: Users,
      subItems: [
        { name: 'New', path: '/n-jio-sevaa/users/new' },
        { name: 'List', path: '/n-jio-sevaa/users/list' }
      ]
    },
    { name: 'Volunteers', path: '/n-jio-sevaa/volunteers-list', icon: LayoutDashboard },
    { name: 'Messages', path: '/n-jio-sevaa/messages', icon: LayoutDashboard },
    { 
      name: 'Settings', 
      icon: Settings,
      subItems: [
        { name: 'Site Settings', path: '/n-jio-sevaa/settings/site-settings' },
        { name: 'Manage Stats', path: '/n-jio-sevaa/settings/stats' },
        { name: 'My Account', path: '/n-jio-sevaa/settings/account' },
        { name: 'User Data', path: '/n-jio-sevaa/settings/data' },
        { name: 'LeaderShip', path: '/n-jio-sevaa/team', icon: LayoutDashboard },
        { name: 'Docs Upload', path: '/n-jio-sevaa/documents', icon: LayoutDashboard },
        // { name: 'Security', path: '/n-jio-sevaa/settings/security' }
      ]
    },
  ];

  // Agar location change ho, aur hum kisi sub-menu path pe ho, toh uska parent auto-expand ho jaye
  useEffect(() => {
    const activeItem = menuItems.find(item => 
      item.subItems?.some(sub => location.pathname.includes(sub.path))
    );
    if (activeItem && isOpen) {
      setExpandedMenu(activeItem.name);
    }
  }, [location.pathname, isOpen]);

  // Dropdown toggle karne ka function
  const handleMenuClick = (menuName: string) => {
    // Agar sidebar shrink hai, toh pehle usko kholo
    if (!isOpen) {
      setIsOpen(true);
      setExpandedMenu(menuName);
    } else {
      // Toggle logic
      setExpandedMenu(expandedMenu === menuName ? null : menuName);
    }
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out
          ${isOpen ? 'w-64 translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-20'}
        `}
      >
        {/* Logo Area */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {isOpen ? (
            <span className="text-xl font-bold text-gray-800 flex items-center gap-2 truncate">
               <div className="w-8 h-8 bg-orange-500 rounded text-white flex items-center justify-center shrink-0">S</div>
               Sevaa Admin
            </span>
          ) : (
            <span className="text-xl font-bold text-orange-500 mx-auto">S</span>
          )}
          
          <button className="lg:hidden text-gray-500 hover:text-gray-900" onClick={() => setIsOpen(false)}>
            <X size={24} />
          </button>
        </div>

        {/* Navigation Menus */}
        <nav className="flex-1 overflow-y-auto py-4 flex flex-col gap-1 px-3 custom-scrollbar">
          <p className={`text-xs font-semibold text-gray-400 mb-2 px-3 ${!isOpen && 'hidden lg:block lg:text-center lg:px-0'}`}>
            {isOpen ? 'NAVIGATION' : 'NAV'}
          </p>

          {menuItems.map((item) => {
            const Icon = item.icon;
            
            // Pata lagana ki parent active hai ya nahi
            const isParentActive = item.path 
              ? location.pathname.includes(item.path) 
              : item.subItems?.some(sub => location.pathname.includes(sub.path));

            const hasSubMenu = !!item.subItems;
            const isExpanded = expandedMenu === item.name && isOpen;

            return (
              <div key={item.name} className="flex flex-col">
                {/* Parent Menu Item */}
                {hasSubMenu ? (
                  // Agar sub-items hain toh isko button banayenge (Toggle ke liye)
                  <button
                    onClick={() => handleMenuClick(item.name)}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-all group outline-none
                      ${isParentActive 
                        ? 'bg-slate-900 text-white shadow-md' 
                        : 'text-gray-600 hover:bg-gray-100 hover:text-slate-900'
                      }
                      ${!isOpen && 'lg:justify-center'}
                    `}
                    title={!isOpen ? item.name : undefined}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} className={isParentActive ? 'text-white' : 'text-gray-500 group-hover:text-slate-900'} />
                      <span className={`font-medium ${!isOpen && 'lg:hidden'}`}>
                        {item.name}
                      </span>
                    </div>
                    {/* Expand/Collapse Arrow */}
                    {isOpen && (
                      isExpanded ? <ChevronDown size={16} className={isParentActive ? 'text-white' : 'text-gray-400'} /> 
                                 : <ChevronRight size={16} className={isParentActive ? 'text-white' : 'text-gray-400'} />
                    )}
                  </button>
                ) : (
                  // Agar sub-items NAHI hain toh direct Link banayenge
                  <Link
                    to={item.path!}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group
                      ${isParentActive 
                        ? 'bg-slate-900 text-white shadow-md' 
                        : 'text-gray-600 hover:bg-gray-100 hover:text-slate-900'
                      }
                      ${!isOpen && 'lg:justify-center'}
                    `}
                    title={!isOpen ? item.name : undefined}
                  >
                    <Icon size={20} className={isParentActive ? 'text-white' : 'text-gray-500 group-hover:text-slate-900'} />
                    <span className={`font-medium ${!isOpen && 'lg:hidden'}`}>
                      {item.name}
                    </span>
                  </Link>
                )}

                {/* Sub Menu Items List */}
                {hasSubMenu && isExpanded && (
                  <div className="flex flex-col gap-1 mt-1 mb-1 pl-9 pr-2 overflow-hidden animate-fade-in-up">
                    {item.subItems!.map((sub) => {
                      const isSubActive = location.pathname === sub.path;
                      return (
                        <Link
                          key={sub.name}
                          to={sub.path}
                          className={`text-sm py-2 px-3 rounded-md transition-colors relative
                            ${isSubActive 
                              ? 'text-orange-600 font-semibold bg-orange-50' 
                              : 'text-gray-500 hover:text-slate-900 hover:bg-gray-50 font-medium'
                            }
                          `}
                        >
                          {/* Indicator Dot for Active state */}
                          {isSubActive && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                          )}
                          {sub.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;